import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";

import {
  subirComprobanteVenta,
  eliminarComprobanteVenta,
} from "../helpers/rutaVentas";

const clean = (v) => String(v ?? "").trim();

const fmtSize = (bytes) => {
  const n = Number(bytes || 0);
  if (!n) return "-";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const fmtDate = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("es-AR");
};

const nombreCliente = (cliente = {}) => {
  if (!cliente || typeof cliente !== "object") return "-";

  const armado = [cliente.apellido1, cliente.apellido2, cliente.nombre]
    .map(clean)
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

  return (
    armado ||
    clean(cliente.razonsocial) ||
    clean(cliente.nombreCompleto) ||
    "-"
  );
};

const isImage = (doc = {}) =>
  String(doc.mimeType || "").startsWith("image/") ||
  /\.(jpg|jpeg|png|webp)$/i.test(String(doc.url || ""));

const getVentaFromResp = (resp, fallback) => {
  if (resp?.venta) return resp.venta;
  if (resp?.data?.venta) return resp.data.venta;
  if (resp?.data) return resp.data;
  return fallback;
};

const ComprobanteCard = ({
  titulo,
  tipo,
  ayuda,
  doc,
  uploading,
  deleting,
  onUpload,
  onDelete,
  soloLectura = false,
}) => {
  const tieneArchivo = !!doc?.url;

  return (
    <div className="border rounded p-3 mb-3" style={{ background: "#fafafa" }}>
      <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{titulo}</div>
          <div className="text-muted" style={{ fontSize: 12 }}>
            {ayuda}
          </div>
        </div>

        <span className={`badge ${tieneArchivo ? "bg-success" : "bg-secondary"}`}>
          {tieneArchivo ? "Cargado" : "Pendiente"}
        </span>
      </div>

      {tieneArchivo ? (
        <div className="mb-2">
          {isImage(doc) ? (
            <a href={doc.url} target="_blank" rel="noreferrer">
              <img
                src={doc.url}
                alt={titulo}
                style={{
                  width: "100%",
                  maxHeight: 220,
                  objectFit: "contain",
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  background: "#fff",
                }}
              />
            </a>
          ) : (
            <div
              className="border rounded p-3 text-center"
              style={{ background: "#fff" }}
            >
              <i
                className="fa fa-file-pdf-o"
                aria-hidden="true"
                style={{ fontSize: 38, color: "#b71c1c" }}
              />
              <div className="mt-2">
                <a href={doc.url} target="_blank" rel="noreferrer">
                  Ver PDF
                </a>
              </div>
            </div>
          )}

          <div className="mt-2" style={{ fontSize: 12 }}>
            <div>
              <strong>Archivo:</strong> {doc.nombreOriginal || "-"}
            </div>
            <div>
              <strong>Tamaño:</strong> {fmtSize(doc.size)}
            </div>
            <div>
              <strong>Fecha:</strong> {fmtDate(doc.fechaSubida)}
            </div>
          </div>
        </div>
      ) : (
        <div className="text-muted mb-2" style={{ fontSize: 13 }}>
          Todavía no se cargó archivo.
        </div>
      )}

        <div className="d-flex gap-2 flex-wrap">
          {!soloLectura && (
            <label className="btn btn-primary btn-sm mb-0">
              {uploading
                ? "Subiendo..."
                : tieneArchivo
                ? "Reemplazar"
                : "Subir"}

              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,application/pdf"
                hidden
                disabled={uploading || deleting}
                onChange={(e) => {
                  const file =
                    e.target.files?.[0];

                  e.target.value = "";

                  if (file) {
                    onUpload(tipo, file);
                  }
                }}
              />
            </label>
          )}

          {tieneArchivo && (
            <>
              <a
                className="btn btn-outline-secondary btn-sm"
                href={doc.url}
                target="_blank"
                rel="noreferrer"
              >
                Ver
              </a>

              {!soloLectura && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  disabled={
                    uploading ||
                    deleting
                  }
                  onClick={() =>
                    onDelete(tipo)
                  }
                >
                  {deleting
                    ? "Eliminando..."
                    : "Eliminar"}
                </Button>
              )}
            </>
          )}
        </div>
    </div>
  );
};

export default function ModalComprobantesVenta({
  show,
  venta,
  onHide,
  onVentaActualizada,
  soloLectura = false,
}) {
  const [localVenta, setLocalVenta] = useState(venta || null);
  const [loadingTipo, setLoadingTipo] = useState("");
  const [deletingTipo, setDeletingTipo] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (show) {
      setLocalVenta(venta || null);
      setError("");
      setLoadingTipo("");
      setDeletingTipo("");
    }
  }, [show, venta]);

  const comprobantes = useMemo(
    () => localVenta?.comprobantes || {},
    [localVenta]
  );

  const handleUpload = async (tipo, file) => {
    if (soloLectura) {
      setError(
        "Esta venta está disponible solamente para consulta."
      );
      return;
    }

    try {
      setError("");

      if (!localVenta?._id) {
        setError("No se encontró el ID de la venta.");
        return;
      }

      const permitidos = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "application/pdf",
      ];

      if (!permitidos.includes(file.type)) {
        setError("Solo se permiten archivos JPG, PNG o PDF.");
        return;
      }

      const max = 3 * 1024 * 1024;

      if (file.size > max) {
        setError("El archivo supera los 3 MB permitidos.");
        return;
      }

      setLoadingTipo(tipo);

      const resp = await subirComprobanteVenta(localVenta._id, tipo, file);
      const actualizada = getVentaFromResp(resp, localVenta);

      setLocalVenta(actualizada);

      if (onVentaActualizada) {
        onVentaActualizada(actualizada);
      }
    } catch (e) {
      console.error("❌ Error subiendo comprobante:", e);
      setError(
        e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          "No se pudo subir el comprobante."
      );
    } finally {
      setLoadingTipo("");
    }
  };

  const handleDelete = async (tipo) => {
    if (soloLectura) {
      setError(
        "Esta venta está disponible solamente para consulta."
      );
      return;
    }

    if (!localVenta?._id) {
      setError("No se encontró el ID de la venta.");
      return;
    }

    if (!window.confirm("¿Eliminar este comprobante?")) return;

    try {
      setError("");
      setDeletingTipo(tipo);

      const resp = await eliminarComprobanteVenta(localVenta._id, tipo);
      const actualizada = getVentaFromResp(resp, localVenta);

      setLocalVenta(actualizada);

      if (onVentaActualizada) {
        onVentaActualizada(actualizada);
      }
    } catch (e) {
      console.error("❌ Error eliminando comprobante:", e);
      setError(
        e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          "No se pudo eliminar el comprobante."
      );
    } finally {
      setDeletingTipo("");
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Comprobantes de Venta</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!localVenta ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <div className="mt-2">Cargando venta...</div>
          </div>
        ) : (
          <>
            <div className="mb-3">
              <div style={{ fontWeight: 700, fontSize: 17 }}>
                Venta N° {localVenta.nroventa || "-"}
              </div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                Cliente: {nombreCliente(localVenta.clienteCompra)} | Total:{" "}
                {Number(localVenta.totalUSD || 0).toLocaleString("es-AR", {
                  style: "currency",
                  currency: "USD",
                })}
              </div>
            </div>

            {soloLectura && (
              <div
                className="alert alert-info py-2"
                style={{ fontSize: 13 }}
              >
                Esta venta pertenece a otra sede.
                Los comprobantes están disponibles
                solamente para consulta.
              </div>
            )}

            {error && (
              <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>
                {error}
              </div>
            )}

            <ComprobanteCard
              titulo="Comprobante Zelle 1"
              tipo="zelle"
              ayuda="Primer comprobante de pago por Zelle. Formatos: JPG, PNG o PDF."
              doc={comprobantes.zelle}
              uploading={loadingTipo === "zelle"}
              deleting={deletingTipo === "zelle"}
              onUpload={handleUpload}
              onDelete={handleDelete}
              soloLectura={soloLectura}
            />

            <ComprobanteCard
              titulo="Comprobante Zelle 2"
              tipo="zelle1"
              ayuda="Segundo comprobante de pago por Zelle. Formatos: JPG, PNG o PDF."
              doc={comprobantes.zelle1}
              uploading={loadingTipo === "zelle1"}
              deleting={deletingTipo === "zelle1"}
              onUpload={handleUpload}
              onDelete={handleDelete}
              soloLectura={soloLectura}
            />

            <ComprobanteCard
              titulo="Comprobante Débito / Crédito"
              tipo="debitoCredito"
              ayuda="Comprobante de pago con tarjeta. Formatos: JPG, PNG o PDF."
              doc={comprobantes.debitoCredito}
              uploading={loadingTipo === "debitoCredito"}
              deleting={deletingTipo === "debitoCredito"}
              onUpload={handleUpload}
              onDelete={handleDelete}
              soloLectura={soloLectura}
            />
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}